import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ToastAction } from "@/components/ui/toast";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { mensagemDeErro, jaEstaNoEstadoPedido } from "@/lib/erro-humano";
import {
  executarOtimista, fazendoNaLista, feitoNaLista, fazendoEmMinhas, feitoEmMinhas,
  type ListaCache, type MinhasCache,
} from "@/lib/otimista-operador";
import { AVISO_CONCLUIDO, ROTULO_DESFAZER, SEGUNDOS_DO_AVISO_DE_DESFAZER } from "@shared/manejo-vocabulario";

// O fluxo de "Fazendo" / "Feito" / "Desfazer" do operador — UM só, usado pela Lista e
// por Minhas. Otimista (a tela muda na hora e volta se falhar), com erro humano
// ("Sem sinal"), 409 de "já está no estado pedido" tratado como sucesso e estado de
// "salvando" POR ITEM (um toque lento não trava a tela inteira).

const CHAVE_LISTA = ["/api/manejo/operacoes", "lista"];
const CHAVE_MINHAS = ["/api/manejo/operacoes", "minhas"];

export function useFluxoOperador() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [pendentes, setPendentes] = useState<Record<string, boolean>>({});

  const marcar = (id: string, valor: boolean) =>
    setPendentes(p => {
      const novo = { ...p };
      if (valor) novo[id] = true; else delete novo[id];
      return novo;
    });

  const erroNaTela = useCallback((erro: unknown) => {
    const h = mensagemDeErro(erro);
    toast({ title: h.titulo, description: h.descricao, variant: "destructive" });
  }, [toast]);

  /** Aplica `fnLista`/`fnMinhas` nos caches e devolve como DESFAZER a aplicação. */
  const aplicar = (
    fnLista: (d: ListaCache) => ListaCache,
    fnMinhas: (d: MinhasCache) => MinhasCache,
  ) => {
    const antesLista = qc.getQueryData<ListaCache>(CHAVE_LISTA);
    const antesMinhas = qc.getQueryData<MinhasCache>(CHAVE_MINHAS);
    return {
      aplicar: () => {
        if (antesLista) qc.setQueryData(CHAVE_LISTA, fnLista(antesLista));
        if (antesMinhas) qc.setQueryData(CHAVE_MINHAS, fnMinhas(antesMinhas));
      },
      reverter: () => {
        if (antesLista) qc.setQueryData(CHAVE_LISTA, antesLista);
        if (antesMinhas) qc.setQueryData(CHAVE_MINHAS, antesMinhas);
      },
    };
  };

  const recarregar = () => qc.invalidateQueries({ queryKey: ["/api/manejo/operacoes"] });

  const iniciar = useCallback(async (id: string) => {
    marcar(id, true);
    const o = aplicar(d => fazendoNaLista(d, id), d => fazendoEmMinhas(d, id));
    const r = await executarOtimista({
      ...o,
      chamar: () => apiRequest("POST", `/api/manejo/operacoes/${id}/iniciar`, {}),
      jaNoEstado: e => jaEstaNoEstadoPedido(e, "em_andamento"),
    });
    marcar(id, false);
    if (!r.ok) erroNaTela(r.erro);
    recarregar();
    return r.ok;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const desfazer = useCallback(async (id: string) => {
    marcar(id, true);
    const r = await executarOtimista({
      aplicar: () => {},
      reverter: () => {},
      chamar: () => apiRequest("POST", `/api/manejo/operacoes/${id}/desfazer`, {}),
      jaNoEstado: e => jaEstaNoEstadoPedido(e, "em_andamento"),
    });
    marcar(id, false);
    if (!r.ok) erroNaTela(r.erro); else toast({ title: "Desfeito", description: "Voltou para Fazendo." });
    recarregar();
    return r.ok;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * "Feito": conclui e mostra "Feito · Desfazer" por 8 s. `corpo` leva as quantidades
   * quando veio da folha de conclusão (item com insumo); sem insumo é só `{}`.
   */
  const concluir = useCallback(async (id: string, corpo: Record<string, unknown> = {}) => {
    marcar(id, true);
    const agoraIso = new Date().toISOString();
    const o = aplicar(d => feitoNaLista(d, id, agoraIso), d => feitoEmMinhas(d, id, agoraIso));
    const r = await executarOtimista({
      ...o,
      chamar: () => apiRequest("POST", `/api/manejo/operacoes/${id}/concluir`, corpo),
      jaNoEstado: e => jaEstaNoEstadoPedido(e, "concluida"),
    });
    marcar(id, false);
    if (!r.ok) {
      erroNaTela(r.erro);
    } else {
      toast({
        title: AVISO_CONCLUIDO,
        duration: SEGUNDOS_DO_AVISO_DE_DESFAZER * 1000,
        action: (
          <ToastAction altText={ROTULO_DESFAZER} onClick={() => { void desfazer(id); }} data-testid="toast-desfazer">
            {ROTULO_DESFAZER}
          </ToastAction>
        ),
      });
    }
    recarregar();
    return r.ok;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desfazer]);

  return { pendentes, iniciar, concluir, desfazer, erroNaTela };
}
